import classNames from "classnames";
import { useCallback, useContext, useMemo } from "react";
import Nbsp from "../../../../../../utils/nbsp";
import { AvisContext } from "../../../../context";
import { IModalConditionalResult } from "../../../modal";
import { IEditableListRowState, IEditableRowState } from "../../types";
import { ActionCancel } from "./ActionCancel";
import { InteractiveBlock } from "../../../form";

interface IAvisEditableListRowActionsProps<P extends IEditableListRowState> {
  index: number;
  value: P;
  disabled?: boolean;
  loading?: boolean;
  hideEdit?: boolean;
  customEditCallback?: (index: number) => void;
  isDeletable?: (fieldValue: P) => boolean;
  confirmBeforeDelete?: (fieldValue: P) => IModalConditionalResult | undefined;
  onStateChange?: (index: number, toState: IEditableRowState) => void;
}

export function AvisEditableListRowActions<P extends IEditableListRowState>(
  props: Readonly<IAvisEditableListRowActionsProps<P>>,
) {
  const {
    value,
    index,
    disabled,
    loading,
    hideEdit,
    isDeletable,
    customEditCallback,
    onStateChange,
  } = props;

  const { res } = useContext(AvisContext);

  const classActions = classNames("avis-editable-list-row-actions");

  const deletable = useMemo(
    () => (isDeletable ? isDeletable?.(value) : true),
    [isDeletable, value],
  );

  const renderActionButtons = useCallback(() => {
    if (loading || disabled) return;
    if (!value.rowState || value.rowState === "View") {
      return (
        <>
          {!hideEdit && (
            <InteractiveBlock
              asLink
              className="row-action"
              onClick={() => {
                if (customEditCallback) {
                  onStateChange?.(index, "Edit");
                  customEditCallback(index);
                  return;
                }

                onStateChange?.(index, "Edit");
              }}
            >
              {res("button_Modify")}
            </InteractiveBlock>
          )}
          <Nbsp />
          {deletable && !disabled && (
            <InteractiveBlock
              asLink
              className="row-action"
              onClick={() => {
                onStateChange?.(index, "Delete");
              }}
            >
              {res("button_Delete")}
            </InteractiveBlock>
          )}
        </>
      );
    }

    return (
      <ActionCancel
        index={index}
        disabled={disabled || (!deletable && value.rowState === "Add")}
        onStateChange={onStateChange}
      />
    );
  }, [
    customEditCallback,
    deletable,
    disabled,
    hideEdit,
    index,
    loading,
    onStateChange,
    res,
    value.rowState,
  ]);

  return <div className={classActions}>{renderActionButtons()}</div>;
}
